import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, ModalBody } from 'reactstrap';

interface MessageContent {
    id: string;
    mime_type: string;
    caption?: string;
}

interface WhatsappMessage {
    id: string;
    type: string;
    content: string | MessageContent;
    createdAt: string;
    [key: string]: any;
}

interface MediaModalProps {
    isOpen: boolean;
    toggle: () => void;
    messages: WhatsappMessage[];
    initialMessageId: string | null;
    getMediaUrl: (mediaId: string, type: string) => Promise<string> | string;
}

const MediaModal: React.FC<MediaModalProps> = ({
                                                   isOpen,
                                                   toggle,
                                                   messages,
                                                   initialMessageId,
                                                   getMediaUrl
                                               }) => {
    const viewportRef = useRef<HTMLDivElement>(null);

    // 1. Mantener orden cronológico: Antiguos (Izquierda) -> Recientes (Derecha)
    const mediaItems = useMemo(() => {
        if (!messages) return [];
        return messages
            .filter(msg => (msg.type === 'image' || msg.type === 'video') && typeof msg.content === 'object');
    }, [messages]);

    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
    const [zoomScale, setZoomScale] = useState<number>(1);

    // 2. Sincronizar el índice inicial al abrir
    useEffect(() => {
        if (isOpen && initialMessageId && mediaItems.length > 0) {
            const index = mediaItems.findIndex(item => item.id === initialMessageId);
            if (index !== -1) {
                setCurrentIndex(index);
                setZoomScale(1);
            }
        }
    }, [isOpen, initialMessageId, mediaItems]);

    // 3. Resolver la URL multimedia activa
    useEffect(() => {
        if (isOpen && mediaItems.length > 0 && mediaItems[currentIndex]) {
            const currentMessage = mediaItems[currentIndex];
            const mediaContent = currentMessage.content as MessageContent;

            const res = getMediaUrl(mediaContent.id, currentMessage.type);
            if (res instanceof Promise) {
                res.then(url => setCurrentUrl(url)).catch(err => console.error(err));
            } else {
                setCurrentUrl(res);
            }
            setZoomScale(1); // Reset zoom al cambiar de archivo
        }
    }, [isOpen, currentIndex, mediaItems, getMediaUrl]);

    // 4. Cargar miniaturas inferiores
    useEffect(() => {
        if (isOpen && mediaItems.length > 0) {
            const resolveThumbnails = async () => {
                const map: Record<string, string> = {};
                for (const item of mediaItems) {
                    const mediaContent = item.content as MessageContent;
                    try {
                        const res = getMediaUrl(mediaContent.id, item.type);
                        map[item.id] = res instanceof Promise ? await res : res;
                    } catch (e) {
                        console.error(e);
                    }
                }
                setThumbnailUrls(map);
            };
            resolveThumbnails();
        }
    }, [isOpen, mediaItems, getMediaUrl]);

    // 5. Handlers de navegación
    const handlePrev = useCallback(() => {
        if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
    }, [currentIndex]);

    const handleNext = useCallback(() => {
        if (currentIndex < mediaItems.length - 1) setCurrentIndex(prev => prev + 1);
    }, [currentIndex, mediaItems.length]);

    // 6. Atajos de teclado
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'Escape') toggle();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handlePrev, handleNext, toggle]);

    // Handlers para botones de Zoom manual
    const handleZoomIn = () => setZoomScale(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoomScale(prev => Math.max(prev - 0.25, 1));

    // 7. CORRECCIÓN DE TIPADO TS: Se recibe un Event genérico y se trata internamente como WheelEvent
    const handleWheelZoom = useCallback((e: Event) => {
        const wheelEvent = e as WheelEvent;
        if (wheelEvent.ctrlKey || wheelEvent.metaKey) {
            wheelEvent.preventDefault(); // Evita que se amplíe toda la página del navegador
            if (wheelEvent.deltaY < 0) {
                setZoomScale(prev => Math.min(prev + 0.20, 3)); // Acercar
            } else {
                setZoomScale(prev => Math.max(prev - 0.20, 1)); // Alejar
            }
        }
    }, []);

    // Escuchador nativo en el contenedor de fondo (Viewport)
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!isOpen || !viewport) return;

        viewport.addEventListener('wheel', handleWheelZoom, { passive: false });
        return () => {
            viewport.removeEventListener('wheel', handleWheelZoom);
        };
    }, [isOpen, handleWheelZoom]);

    // Referencia y efecto para registrar el evento directamente sobre la Imagen o Video activo
    const mediaRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);
    useEffect(() => {
        const targetMedia = mediaRef.current;
        if (!isOpen || !targetMedia) return;

        targetMedia.addEventListener('wheel', handleWheelZoom, { passive: false });
        return () => {
            targetMedia.removeEventListener('wheel', handleWheelZoom);
        };
    }, [isOpen, currentUrl, currentIndex, handleWheelZoom]);


    if (!isOpen || mediaItems.length === 0) return null;

    const currentMessage = mediaItems[currentIndex];
    const caption = (currentMessage.content as MessageContent).caption;

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="xl"
            fade
            contentClassName="bg-transparent border-0 h-100 shadow-none d-flex flex-column justify-content-between"
            modalClassName="p-0"
            style={{ maxWidth: '100vw', margin: 0 }}
        >
            <style>
                {`
                    .modal-backdrop.show {
                        opacity: 0.96 !important;
                        background-color: #0b141a !important;
                    }
                    .nav-media-btn {
                        width: 50px;
                        height: 50px;
                        background-color: #d19221 !important;
                        border: none;
                        backdrop-filter: blur(4px);
                        color: white;
                        transition: all 0.2s;
                        z-index: 1060;
                    }
                    .nav-media-btn:hover:not(:disabled) {
                        opacity: 0.9;
                        transform: scale(1.08);
                    }
                    .nav-media-btn:disabled {
                        opacity: 0.15;
                        cursor: not-allowed;
                        background-color: rgba(11, 20, 26, 0.5) !important;
                    }
                    .thumb-item-wrapper {
                        width: 55px;
                        height: 55px;
                        cursor: pointer;
                        border: 2px solid transparent;
                        transition: all 0.2s;
                        border-radius: 6px;
                        overflow: hidden;
                        opacity: 0.4;
                        flex-shrink: 0;
                    }
                    .thumb-item-wrapper:hover {
                        opacity: 0.8;
                    }
                    .thumb-item-wrapper.active {
                        border-color: #00a884;
                        opacity: 1;
                        transform: scale(1.05);
                    }
                    .thumb-img, .thumb-vid {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                    
                    .viewport-container {
                        overflow: auto;
                        height: calc(100vh - 140px);
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        position: relative;
                    }
                    .zoomable-content {
                        transform-origin: center center;
                        transition: transform 0.15s ease-out;
                        pointer-events: auto;
                    }
                    
                    .zoom-controls-panel {
                        background-color: rgba(34, 46, 53, 0.85);
                        backdrop-filter: blur(8px);
                        border-radius: 8px;
                        padding: 6px 12px;
                        gap: 8px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                        pointer-events: auto;
                        border: 1px solid rgba(255,255,255,0.05);
                    }
                    .zoom-panel-btn {
                        background: transparent;
                        border: none;
                        color: #aebac1;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: background-color 0.15s, color 0.15s;
                    }
                    .zoom-panel-btn:hover {
                        background-color: rgba(255,255,255,0.08);
                        color: #fff;
                    }
                    .zoom-percentage-badge {
                        background-color: rgba(255,255,255,0.08);
                        color: #e9edef;
                        font-size: 13px;
                        padding: 4px 10px;
                        border-radius: 14px;
                        min-width: 55px;
                        text-align: center;
                        font-weight: 500;
                    }
                `}
            </style>

            {/* BOTÓN CERRAR FLOTANTE */}
            <div
                className="d-flex align-items-center justify-content-end p-3 position-absolute top-0 end-0 w-100"
                style={{ zIndex: 1070, pointerEvents: 'none' }}
            >
                <button
                    onClick={toggle}
                    className="btn rounded-circle d-flex align-items-center justify-content-center text-white"
                    style={{
                        width: '42px',
                        height: '42px',
                        backgroundColor: 'rgba(11, 20, 26, 0.4)',
                        border: 'none',
                        backdropFilter: 'blur(4px)',
                        pointerEvents: 'auto',
                        cursor: 'pointer'
                    }}
                >
                    <i className="ri-close-line fs-24"></i>
                </button>
            </div>

            {/* CONTENEDOR MULTIMEDIA PRINCIPAL */}
            <ModalBody
                className="p-0 d-flex flex-column align-items-center justify-content-center position-relative w-100"
                style={{ height: '100vh', overflow: 'hidden', backgroundColor: 'transparent' }}
            >
                <div className="d-flex align-items-center justify-content-between w-100 h-100 position-relative px-4">

                    {/* Flecha Izquierda */}
                    <button
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        className="btn rounded-circle nav-media-btn position-absolute start-0 ms-4"
                    >
                        <i className="ri-arrow-left-s-line fs-26"></i>
                    </button>

                    {/* Visor de Contenido */}
                    <div ref={viewportRef} className="viewport-container px-5">
                        {currentUrl ? (
                            currentMessage.type === 'video' ? (
                                <video
                                    key={currentUrl}
                                    ref={(node) => { mediaRef.current = node; }}
                                    src={currentUrl}
                                    controls
                                    autoPlay
                                    className="shadow-lg zoomable-content"
                                    style={{
                                        maxWidth: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        outline: 'none',
                                        transform: `scale(${zoomScale})`
                                    }}
                                />
                            ) : (
                                <img
                                    ref={(node) => { mediaRef.current = node; }}
                                    src={currentUrl}
                                    alt={caption || 'Imagen adjunta'}
                                    className="shadow-lg zoomable-content"
                                    style={{
                                        maxWidth: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        transform: `scale(${zoomScale})`
                                    }}
                                />
                            )
                        ) : (
                            <div className="text-light spinner-border" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        )}

                        {/* CONTROLES DE ZOOM FLOTANTES */}
                        {currentUrl && (
                            <div
                                className="position-absolute bottom-0 end-0 mb-3 me-5"
                                style={{ zIndex: 1065, pointerEvents: 'none' }}
                            >
                                <div className="d-flex align-items-center zoom-controls-panel">
                                    <button
                                        className="zoom-panel-btn"
                                        onClick={handleZoomIn}
                                        title="Acercar"
                                        disabled={zoomScale >= 3}
                                    >
                                        <i className="ri-add-line fs-18"></i>
                                    </button>
                                    <button
                                        className="zoom-panel-btn"
                                        onClick={handleZoomOut}
                                        title="Alejar"
                                        disabled={zoomScale <= 1}
                                    >
                                        <i className="ri-subtract-line fs-18"></i>
                                    </button>
                                    <div className="zoom-percentage-badge">
                                        {Math.round(zoomScale * 100)}%
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Flecha Derecha */}
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === mediaItems.length - 1}
                        className="btn rounded-circle nav-media-btn position-absolute end-0 me-4"
                    >
                        <i className="ri-arrow-right-s-line fs-26"></i>
                    </button>
                </div>

                {/* PIE DE PÁGINA (CAPTION + MINIATURAS) */}
                <div
                    className="w-100 d-flex flex-column align-items-center py-3"
                    style={{
                        backgroundColor: '#0b141a',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        zIndex: 1055
                    }}
                >
                    {caption && (
                        <div className="text-center text-white font-size-15 mb-3 px-4" style={{ maxWidth: '750px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {caption}
                        </div>
                    )}

                    {/* Carrusel de Miniaturas */}
                    {mediaItems.length > 1 && (
                        <div className="d-flex gap-2 justify-content-center align-items-center overflow-auto px-3 w-100" style={{ maxHeight: '65px' }}>
                            {mediaItems.map((item, index) => {
                                const thumbUrl = thumbnailUrls[item.id];
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => setCurrentIndex(index)}
                                        className={`thumb-item-wrapper ${index === currentIndex ? 'active' : ''}`}
                                    >
                                        {item.type === 'video' ? (
                                            <div className="position-relative w-100 h-100 bg-black">
                                                {thumbUrl && <video src={thumbUrl} className="thumb-vid" />}
                                                <i className="ri-play-fill position-absolute top-50 start-50 translate-middle text-white fs-16"></i>
                                            </div>
                                        ) : (
                                            <div className="w-100 h-100 bg-black">
                                                {thumbUrl && <img src={thumbUrl} alt="miniatura" className="thumb-img" />}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};

export default MediaModal;
